import type { OnboardingApplicationRecord } from "./driver-api.ts";
import type { ChipClass } from "./shift-readiness.ts";
import type { CheckProps } from "./ui/PhoneShell.tsx";

/**
 * `Onboarding board` — the application a driver is waiting on, read back at last.
 *
 * The design's screen, value for value from `15 WP14 Driver Onboarding and Earnings.dc.html`:
 *
 *     board:{l:'Onboarding board',art:'idle',on:0,chip:['waiting','in_review'],
 *       title:'You’re nearly there',
 *       sub:'We’re checking your documents. Most reviews finish within 1 business day.',
 *       checks:[['ok','person','Profile','Done 26 Sep'],
 *               ['ok','badge','Driving licence','Approved 27 Sep'],
 *               ['wait','description','PrDP permit','In review · since 28 Sep'],
 *               ['bad','two_wheeler','Vehicle photos','Action needed: the plate isn’t readable'],
 *               ['wait','verified_user','Background check','With our partner · 2–3 days']]}
 *
 * **This screen was blocked on a service that could only be written to.** DRV-02 published three
 * commands and no read of any kind, so an application could be created, have its steps verified and
 * be moved through ten statuses while the applicant had no operation that would tell them any of
 * it. ADR 0076 publishes the read and the step reason together, because one without the other is a
 * board that can say a step needs attention and not what attention.
 *
 * ## Five designed rows against three published steps
 *
 * The design draws Profile, Driving licence, PrDP permit, Vehicle photos and Background check.
 * DRV-02 models three step kinds — `identity`, `documents`, `vehicle` — and `stepKinds` is what
 * `createApplication` seeds every application from, so three is not a sample size. **One row per
 * published step is drawn and the count is not padded**, because the alternative is five rows of
 * which two are invented: a `Background check` row with no step behind it would sit there saying
 * `pending` forever, and a driver would wait for something no service is doing. Q-224.
 *
 * The three glyphs are the design's own, taken from the rows whose subject matches:
 * `person` for identity, `description` for documents, `two_wheeler` for the vehicle.
 *
 * ## What each row can say, and the one date it cannot
 *
 * A step publishes a kind, a status, a reference and a reason. The design's sub-lines are
 * `Done 26 Sep`, `Approved 27 Sep` and `In review · since 28 Sep` — **every one of them a date**,
 * and DRV-02 timestamps the application rather than the step. So a verified step says it was
 * verified and names what against; it does not say when, and that absence is drawn rather than
 * filled from `updatedAt`, which is when the application last moved for any reason and would be
 * wrong on every step but the most recent. Q-225.
 *
 * The `action_required` row is the one that is fully drawn, and it is the row the screen exists
 * for: the reviewer's own sentence, shown to the driver unchanged.
 */

/** DRV-02's three kinds, with the design's glyph and the design's label for each. */
const STEP_ROWS: Readonly<Record<string, { readonly glyph: string; readonly label: string }>> = {
  identity: { glyph: "person", label: "Profile" },
  documents: { glyph: "description", label: "Driving licence" },
  vehicle: { glyph: "two_wheeler", label: "Vehicle photos" },
};

/** No per-step instant is published, so no row says when it moved. */
export const NO_STEP_INSTANT =
  "DRV-02 timestamps the application and not the step, so there is no date for this row";

/** One check row per published step, in the order DRV-02 seeds them. */
export function stepChecks(application: OnboardingApplicationRecord): CheckProps[] {
  return application.steps.map((step) => {
    const row = STEP_ROWS[step.kind] ?? { glyph: "help", label: step.kind };
    if (step.status === "action_required") {
      return {
        key: step.kind,
        status: "bad" as const,
        glyph: row.glyph,
        /*
         * The design's own sentence shape — `Action needed: <reason>` — with the reviewer's words
         * after the colon. A step in this state always has a reason: the service refuses the
         * command without one, which is what makes this safe to render without a fallback sentence.
         */
        title: row.label,
        sub: `Action needed: ${step.reason ?? "no reason was recorded"}`,
      };
    }
    if (step.status === "verified") {
      return {
        key: step.kind,
        status: "ok" as const,
        glyph: row.glyph,
        title: row.label,
        // `Approved 27 Sep` in the design. The word is published and the date is not.
        sub: NO_STEP_INSTANT,
        subIsAbsence: true,
      };
    }
    return {
      key: step.kind,
      status: "wait" as const,
      glyph: row.glyph,
      title: row.label,
      // `In review · since 28 Sep`. The first half is the status; the second has no source.
      sub: NO_STEP_INSTANT,
      subIsAbsence: true,
    };
  });
}

/**
 * The chip, which is the application's own status and nothing derived.
 *
 * The design draws `['waiting','in_review']` — the class and the published word. DRV-02 publishes
 * ten statuses and the design samples one, so the word is taken from the record and the class is
 * decided here, per status, rather than fixed at `waiting`.
 */
export function boardChip(application: OnboardingApplicationRecord): {
  readonly class: ChipClass;
  readonly label: string;
} {
  return { class: chipClassFor(application.status), label: application.status };
}

function chipClassFor(status: string): ChipClass {
  switch (status) {
    case "approved":
    case "conditionally_approved":
      return "done";
    case "rejected":
    case "withdrawn":
      return "closed";
    case "action_required":
      return "caution";
    case "draft":
      return "idle";
    default:
      // `submitted`, `in_review`, `verification_pending`, `decision_pending`: the platform has it
      // and the driver is waiting. The design's own class for this screen's sampled status.
      return "waiting";
  }
}

/**
 * The headline, which is the design's sentence only where the design's status holds.
 *
 * `You're nearly there` is approved copy for an application under review. It would be false on a
 * rejected one and cruel on one needing action, so it is drawn for the statuses it was written for
 * and replaced elsewhere — and every replacement is flagged as this build's wording rather than the
 * design's, which is the mechanism `Vehicle` uses for the same problem.
 */
export function boardTitle(application: OnboardingApplicationRecord): {
  readonly text: string;
  readonly designed: boolean;
} {
  switch (application.status) {
    case "submitted":
    case "in_review":
    case "verification_pending":
    case "decision_pending":
      return { text: "You're nearly there", designed: true };
    case "action_required":
      return { text: "There's one thing to fix", designed: false };
    case "approved":
    case "conditionally_approved":
      return { text: "You're approved", designed: false };
    case "rejected":
      return { text: "This application was not approved", designed: false };
    case "withdrawn":
      return { text: "This application was withdrawn", designed: false };
    default:
      return { text: "Your application is a draft", designed: false };
  }
}

/**
 * The sub-line, under the same rule as the title.
 *
 * The design's sentence carries a service-level promise — _most reviews finish within 1 business
 * day_ — which nothing on this platform measures. It is drawn **as the design wrote it** on the
 * statuses it was written for, because it is approved copy and the house rules keep copy word for
 * word; it is not extended to any other status, because repeating a promise about review times to
 * somebody whose application was rejected is worse than saying nothing.
 */
export function boardSub(application: OnboardingApplicationRecord): {
  readonly text: string;
  readonly designed: boolean;
} {
  const waiting = ["submitted", "in_review", "verification_pending", "decision_pending"];
  if (waiting.includes(application.status)) {
    return {
      text: "We're checking your documents. Most reviews finish within 1 business day.",
      designed: true,
    };
  }
  if (application.status === "action_required") {
    const named = application.steps.filter((step) => step.status === "action_required").length;
    return {
      text:
        named === 1
          ? "One step needs your attention before we can carry on."
          : `${String(named)} steps need your attention before we can carry on.`,
      designed: false,
    };
  }
  return { text: `This application is ${application.status}.`, designed: false };
}
